import React, { useState, useEffect } from 'react';
import { API_URL } from '../../services/api';
import { useNavigate, useParams } from 'react-router-dom';
import {
  TextField,
  Button,
  Typography,
  Box,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  OutlinedInput,
  SelectChangeEvent,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import axios from 'axios';

interface Category {
  id: number;
  name: string;
}

const AdminNewsForm: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    excerpt: '',
    status: 'draft',
  });
  const [selectedCategories, setSelectedCategories] = useState<number[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [currentImage, setCurrentImage] = useState<string | null>(null);

  useEffect(() => {
    fetchCategories();
  }, []);

  useEffect(() => {
    if (id) {
      fetchNews();
    }
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/categories`);
      setCategories(response.data);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const fetchNews = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/news/id/${id}`);
      setFormData({
        title: response.data.title,
        content: response.data.content,
        excerpt: response.data.excerpt || '',
        status: response.data.status,
      });
      setSelectedCategories(response.data.categories.map((cat: any) => cat.id));
      setCurrentImage(response.data.featured_image || null);
    } catch (error) {
      console.error('Error fetching news:', error);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleCategoryChange = (event: SelectChangeEvent<typeof selectedCategories>) => {
    const value = event.target.value;
    setSelectedCategories(typeof value === 'string' ? value.split(',').map(Number) : value);
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setImageFile(e.target.files[0]);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const token = localStorage.getItem('token');
    const formDataToSend = new FormData();

    Object.keys(formData).forEach(key => {
      formDataToSend.append(key, (formData as any)[key]);
    });

    if (imageFile) {
      formDataToSend.append('newsImage', imageFile);
    }

    if (currentImage) {
      formDataToSend.append('currentImage', currentImage);
    }

    formDataToSend.append('categories', JSON.stringify(selectedCategories));

    try {
      if (id) {
        await axios.put(`${API_URL}/api/news/${id}`, formDataToSend, {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data',
          },
        });
      } else {
        await axios.post(`${API_URL}/api/news`, formDataToSend, {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data',
          },
        });
      }
      navigate('/admin/news');
    } catch (error) {
      console.error('Error saving news:', error);
    }
  };

  return (
    <Paper sx={{ p: 4, background: 'linear-gradient(135deg, #1a1a1a 0%, #2a2a2a 100%)' }}>
      <Typography variant="h4" sx={{ mb: 4, fontWeight: 600 }}>
        {id ? 'Editar' : 'Nueva'} Noticia
      </Typography>

      <Box component="form" onSubmit={handleSubmit}>
        <TextField
          fullWidth
          label="Título"
          name="title"
          value={formData.title}
          onChange={handleChange}
          required
          sx={{ mb: 3 }}
        />

        <TextField
          fullWidth
          label="Extracto"
          name="excerpt"
          value={formData.excerpt}
          onChange={handleChange}
          multiline
          rows={2}
          sx={{ mb: 3 }}
        />

        <TextField
          fullWidth
          label="Contenido"
          name="content"
          value={formData.content}
          onChange={handleChange}
          multiline
          rows={10}
          required
          sx={{ mb: 3 }}
        />

        <FormControl fullWidth sx={{ mb: 3 }}>
          <InputLabel>Categorías</InputLabel>
          <Select
            multiple
            value={selectedCategories}
            onChange={handleCategoryChange}
            input={<OutlinedInput label="Categorías" />}
            renderValue={(selected) => (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {selected.map((value) => {
                  const category = categories.find(c => c.id === value);
                  return <Chip key={value} label={category?.name || ''} />;
                })}
              </Box>
            )}
          >
            {categories.map((category) => (
              <MenuItem key={category.id} value={category.id}>
                {category.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl fullWidth sx={{ mb: 3 }}>
          <InputLabel>Estado</InputLabel>
          <Select
            name="status"
            value={formData.status}
            onChange={(e) => setFormData({ ...formData, status: e.target.value })}
            label="Estado"
          >
            <MenuItem value="draft">Borrador</MenuItem>
            <MenuItem value="published">Publicado</MenuItem>
          </Select>
        </FormControl>

        {currentImage && !imageFile && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="body2" sx={{ mb: 1 }}>Imagen actual:</Typography>
            <img
              src={`${API_URL}${currentImage}`}
              alt="Imagen actual"
              style={{ maxWidth: '200px', maxHeight: '150px', objectFit: 'cover', borderRadius: '8px' }}
            />
          </Box>
        )}

        <Button
          variant="contained"
          component="label"
          sx={{ mb: 3 }}
        >
          {currentImage ? 'Cambiar Imagen' : 'Subir Imagen'}
          <input
            type="file"
            hidden
            accept="image/*"
            onChange={handleImageChange}
          />
        </Button>
        {imageFile && (
          <Typography variant="body2" sx={{ ml: 2, display: 'inline' }}>
            Nueva: {imageFile.name}
          </Typography>
        )}

        <Box sx={{ display: 'flex', gap: 2, mt: 4 }}>
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/admin/news')}
          >
            Volver
          </Button>
          <Button
            type="submit"
            variant="contained"
            color="primary"
            startIcon={<SaveIcon />}
          >
            Guardar
          </Button>
        </Box>
      </Box>
    </Paper>
  );
};

export default AdminNewsForm;